import React, { useEffect, useState } from "react";
import { gql } from "@apollo/client";
import { styled } from "@material-ui/styles";
import { Theme, Box } from "@material-ui/core";
import Content from "../content";
import { useQuery, useMutation } from "@apollo/client";
import Typography from "../typography";
import { IPlayer, ITeam } from "../../types";
import { injectIntl } from "react-intl";
import InitialCard from "./InitialCard";
import AssignTeamModal from "../assign-team-modal";
import SnackBar from "../snackbar";
import CancelIcon from "@material-ui/icons/CancelOutlined";
import AccountCircleIcon from "@material-ui/icons/AccountCircle";
import GroupWorkIcon from "@material-ui/icons/GroupWork";
import { IStyledArguments } from "../../types/styled-arguments";
import GreetingHeader from "./GreetingHeader";
import NextEventHero from "./NextEventHero";
import DashboardTeamCard from "./DashboardTeamCard";
import WeekStrip from "./WeekStrip";
import TeamNextMatch from "./TeamNextMatch";
import messages from "./PageDashboard.intl";

export const ALL_TEAMS = gql`
  query GetTeams {
    allTeams {
      id
      name
      trainingSetup {
        monday
        tuesday
        wednesday
        thursday
        friday
        saturday
        sunday
      }
      nextTraining {
        trainingAttendance {
          player {
            id
            name
            surname
          }
          attendance
        }
        date
      }
      squad {
        id
        name
        surname
        number
        position
      }
      matches {
        id
        score {
          home
          away
        }
        isWon
        isTied
        isLost
      }
    }
    allPlayers {
      id
      name
      surname
      team {
        id
        name
      }
    }
    currentUser {
      id
      role
    }
  }
`;

const ASSIGN_PLAYER_TO_TEAM = gql`
  mutation AssignTeamToPlayer($playerId: ID!, $teamId: ID!) {
    assignPlayerToTeam(playerId: $playerId, teamId: $teamId) {
      op
      error
    }
  }
`;

const MainContent = styled(Content)<Theme>(({ theme }) => ({
  padding: theme.spacing(2.5),
  paddingBottom: 96,
  background: theme.palette.extra.backgroundGrey,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2.5),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
    paddingBottom: 96,
    gap: theme.spacing(2),
  },
}));

const StatsRow = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  gap: theme.spacing(1.25),
}));

const StatTile = styled("div")(({ theme }: IStyledArguments) => ({
  flex: 1,
  textAlign: "center",
  background: "#ffffff",
  border: `1px solid ${theme.palette.extra.borderColor}`,
  borderRadius: 14,
  padding: theme.spacing(1.75, 0.5),
}));

const StatValue = styled("div")(({ theme }: IStyledArguments) => ({
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 800,
  fontSize: "1.4rem",
  color: theme.palette.text.primary,
  fontVariantNumeric: "tabular-nums",
}));

const StatLabel = styled("div")(({ theme }: IStyledArguments) => ({
  fontSize: "0.65rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: 0.4,
  color: theme.palette.text.secondary,
  marginTop: 3,
}));

const SectionHeader = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 12,
});

const SectionTitle = styled("span")(({ theme }: IStyledArguments) => ({
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 700,
  fontSize: "1.05rem",
  color: theme.palette.text.primary,
}));

const TeamsSection = styled("div")({
  minWidth: 0,
  width: "100%",
});

const TeamsScroll = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  gap: theme.spacing(1.5),
  overflowX: "auto",
  paddingBottom: 4,
  minWidth: 0,
}));

const MatchesSection = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const PendingBanner = styled("button")(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  width: "100%",
  gap: theme.spacing(1.5),
  background: theme.palette.extra.warningSoft,
  border: `1px solid rgba(245, 124, 0, 0.25)`,
  borderRadius: 14,
  padding: theme.spacing(1.75, 2),
  textAlign: "left",
  cursor: "pointer",
  appearance: "none",
  font: "inherit",
}));

const PendingBannerIcon = styled("div")(({ theme }: IStyledArguments) => ({
  width: 36,
  height: 36,
  borderRadius: "50%",
  background: "#ffffff",
  border: `1px solid rgba(245, 124, 0, 0.35)`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  "& svg": {
    color: theme.palette.extra.warning,
  },
}));

const PendingBannerText = styled("div")({
  flex: 1,
});

const PendingBannerTitle = styled("div")(({ theme }: IStyledArguments) => ({
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 600,
  fontSize: "0.9rem",
  color: theme.palette.text.primary,
}));

const PendingBannerSubtitle = styled("div")(({ theme }: IStyledArguments) => ({
  fontSize: "0.75rem",
  color: theme.palette.text.secondary,
  marginTop: 1,
}));

const PendingPlayerCard = styled("div")(({ theme }: IStyledArguments) => ({
  background: theme.palette.background.paper,
  borderRadius: 16,
  border: `1px solid ${theme.palette.extra.borderColor}`,
  boxShadow: "0 1px 3px rgba(16, 21, 26, 0.06)",
  padding: theme.spacing(2.5),
  [theme.breakpoints.down("sm")]: {
    borderRadius: 14,
    padding: theme.spacing(2),
  },
}));

const PlayerInfoSection = styled(Box)<Theme>(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const PlayerAvatar = styled("div")<Theme>(({ theme }) => ({
  width: 56,
  height: 56,
  borderRadius: "50%",
  background: theme.palette.primary.main,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  "& svg": {
    fontSize: "2rem",
    color: theme.palette.primary.contrastText,
  },
}));

const PlayerDetails = styled(Box)<Theme>(({ theme }) => ({
  flex: 1,
  "& h6": {
    margin: 0,
    fontWeight: 700,
    color: theme.palette.text.primary,
    fontSize: "1.05rem",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
  },
  "& .status": {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(0.75),
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
    fontWeight: 500,
    "& svg": {
      fontSize: "1.15rem",
    },
  },
}));

const PendingBadge = styled("span")<Theme>(({ theme }) => ({
  background: theme.palette.extra.warningSoft,
  border: "1px solid rgba(245, 124, 0, 0.3)",
  borderRadius: 8,
  padding: theme.spacing(0.5, 1.5),
  fontSize: "0.72rem",
  fontWeight: 600,
  color: theme.palette.extra.warning,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
}));

const PendingListWrapper = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(1.5),
}));

const CoachDashboard = injectIntl<{ user: any }>(({ user, intl }) => {
  const { data: teamsData, loading: teamsLoading, refetch } = useQuery<any>(ALL_TEAMS);
  const [teams, setTeams] = useState<ITeam[]>();
  const [playersWithoutTeam, setPlayersWithoutTeam] = useState<IPlayer[]>([]);
  const [assignTeam, { data: assignData }] = useMutation(ASSIGN_PLAYER_TO_TEAM);
  const [showMessage, setShowMessage] = useState(false);
  const [showPending, setShowPending] = useState(false);

  useEffect(() => {
    if (teamsData && teamsData.allTeams) {
      setTeams(teamsData.allTeams);
    }
    if (teamsData && teamsData.allPlayers) {
      const playersToAssign = [];
      for (let p of teamsData.allPlayers) {
        if (p.team === null) {
          playersToAssign.push(p);
        }
      }
      setPlayersWithoutTeam(playersToAssign);
    }
  }, [teamsData]);

  useEffect(() => {
    if (assignData) {
      refetch();
    }
  }, [assignData]);

  const handleAssingPlayerIntoTeam = (playerId: string, teamId: string) => {
    assignTeam({
      variables: {
        playerId,
        teamId,
      },
    });
  };

  const totalPlayers =
    teams && teams.map((team) => (team.squad && team.squad.length) || 0).reduce((acc, curr) => acc + curr, 0);
  const totalMatches =
    teams && teams.map((team) => (team.matches && team.matches.length) || 0).reduce((acc, curr) => acc + curr, 0);

  return (
    <MainContent container alignItems="stretch" justify="flex-start">
      {user && (
        <>
          <GreetingHeader
            user={user}
            subtitle={
              teams && teams.length > 0
                ? `${intl.formatMessage(messages.manager)} · ${teams[0].name}`
                : undefined
            }
          />

          {!teamsLoading && (!teams || teams.length === 0) && (
            <InitialCard user={user} callback={() => refetch()} />
          )}

          {teams && teams.length > 0 && (
            <>
              <NextEventHero teams={teams} />

              <StatsRow>
                <StatTile>
                  <StatValue>{teams.length}</StatValue>
                  <StatLabel>{intl.formatMessage(messages.statTeams)}</StatLabel>
                </StatTile>
                <StatTile>
                  <StatValue>{totalPlayers || 0}</StatValue>
                  <StatLabel>{intl.formatMessage(messages.statPlayers)}</StatLabel>
                </StatTile>
                <StatTile>
                  <StatValue>{totalMatches || 0}</StatValue>
                  <StatLabel>{intl.formatMessage(messages.statMatches)}</StatLabel>
                </StatTile>
              </StatsRow>

              <TeamsSection>
                <SectionHeader>
                  <SectionTitle>{intl.formatMessage(messages.yourTeams)}</SectionTitle>
                </SectionHeader>
                <TeamsScroll>
                  {teams.map((team) => (
                    <DashboardTeamCard key={team.id} team={team} />
                  ))}
                </TeamsScroll>
              </TeamsSection>

              <WeekStrip teams={teams} />

              <MatchesSection>
                {teams.map((team) => (
                  <TeamNextMatch key={team.id} teamId={team.id} />
                ))}
              </MatchesSection>
            </>
          )}

          {playersWithoutTeam && playersWithoutTeam.length > 0 && (
            <div>
              <PendingBanner type="button" onClick={() => setShowPending((s) => !s)}>
                <PendingBannerIcon>
                  <GroupWorkIcon />
                </PendingBannerIcon>
                <PendingBannerText>
                  <PendingBannerTitle>
                    {intl.formatMessage(messages.pendingPlayersBanner, { n: playersWithoutTeam.length })}
                  </PendingBannerTitle>
                  <PendingBannerSubtitle>{intl.formatMessage(messages.tapToAssign)}</PendingBannerSubtitle>
                </PendingBannerText>
              </PendingBanner>

              {showPending && (
                <PendingListWrapper>
                  {playersWithoutTeam.map((player: any) => (
                    <PendingPlayerCard key={`player-${player.id}`}>
                      <PlayerInfoSection>
                        <PlayerAvatar>
                          <AccountCircleIcon />
                        </PlayerAvatar>
                        <PlayerDetails>
                          <Typography.H6>
                            {player.name} {player.surname}
                            <PendingBadge>Pending</PendingBadge>
                          </Typography.H6>
                          <div className="status">
                            <GroupWorkIcon />
                            <span>Waiting for team assignment</span>
                          </div>
                        </PlayerDetails>
                      </PlayerInfoSection>
                      <AssignTeamModal player={player} teams={teams} callback={handleAssingPlayerIntoTeam} />
                    </PendingPlayerCard>
                  ))}
                </PendingListWrapper>
              )}
            </div>
          )}
        </>
      )}
      <SnackBar
        data-testid="delete-error"
        open={showMessage}
        message="Player has been assigned"
        variant="success"
        action={() => {
          setShowMessage(false);
        }}
        actionText={<CancelIcon />}
      />
    </MainContent>
  );
});

export default CoachDashboard;
